import React, { useRef } from 'react';
import { Animated, Platform, View } from 'react-native';
import { FloatingAction } from 'react-native-floating-action';
import MyAppText from './MyAppText';
import { createStyleSheet, UnistylesRuntime, useStyles } from 'react-native-unistyles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus } from '@fortawesome/free-solid-svg-icons';
import { IconProp } from '@fortawesome/fontawesome-svg-core';

interface IButtonFloatingAction {
    name: string;
    label?: string;
    icon?: IconProp;
    color?: string;
    onPress: () => void;
}

interface IButtonFloatingActionOverride extends IButtonFloatingAction {
    icon: IconProp;
}

interface Offset {
    vertical?: number;
    horizontal?: number;
}

type Props =
    | {
        overrideWithAction: true;
        actions: [IButtonFloatingActionOverride];
        visible?: boolean;
        offset?: Offset;
    }
    | {
        overrideWithAction?: false;
        actions: IButtonFloatingAction[];
        visible?: boolean;
        offset?: Offset;
    };

export default function FloatingButton({ actions, offset, overrideWithAction, visible = true }: Props) {
    const { styles, theme } = useStyles(styleSheet);

    const rotation = useRef(new Animated.Value(0)).current;

    const animateIcon = (open: boolean) => {
        Animated.timing(rotation, {
            toValue: open ? 1 : 0,
            duration: 250,
            useNativeDriver: true,
        }).start();
    };

    const rotateInterpolate = rotation.interpolate({
        inputRange: [0, 1],
        outputRange: ['0deg', '45deg'],
    });


    function getIconColor(action: IButtonFloatingAction) {
        if (action.color) {
            return action.color;
        } else if (UnistylesRuntime.themeName === 'dark') {
            return '#000000';
        } else {
            return theme.colors.primary['500'];
        }
    }

    function getButtonBorderColor(action: IButtonFloatingAction) {
        if (action.color) {
            return action.color;
        } else if (UnistylesRuntime.themeName === 'dark') {
            return theme.colors.white['500'];
        } else {
            return theme.colors.primary['500'];
        }
    }

    const mappedActions = overrideWithAction
        ? actions.map(action => ({
            name: action.name,
            margin: 0,
            icon: <FontAwesomeIcon icon={action.icon} color={theme.colors.white['500']} fontSize={24} style={{ padding: 16 }} />
        }))
        : actions.map(action => ({
            name: action.name,
            render: () => <View style={{ flexDirection: 'row', gap: 12, alignItems: 'center', paddingRight: 4 }}>
                {action.label && <MyAppText style={[styles.itemText, styles.dropShadow]}>{action.label}</MyAppText>}
                {action.icon && <View style={[styles.itemButton(getButtonBorderColor(action)), styles.dropShadow]}>
                    <FontAwesomeIcon icon={action.icon} color={getIconColor(action)} fontSize={24} style={{ padding: 16 }} />
                </View>}
            </View>,
            margin: 0
        }));

    return (
        <FloatingAction
            visible={visible}
            actions={mappedActions}
            onPressItem={(name) => {
                // This is to avoid the component to keep the focus and break stuff
                if (Platform.OS === 'web' && typeof document !== 'undefined') {
                    (document.activeElement as HTMLElement | null)?.blur();
                    document.body?.focus({ preventScroll: true });
                }

                const selectedAction = actions.find(item => item.name === name);
                if (selectedAction && selectedAction.onPress) {
                    selectedAction.onPress();
                }
            }}
            animated
            buttonSize={64}
            color={theme.colors.primary['500']}
            onOpen={() => animateIcon(true)}
            onClose={() => animateIcon(false)}
            actionsPaddingTopBottom={3}
            distanceToEdge={{ vertical: offset?.vertical ?? 26, horizontal: offset?.horizontal ?? 32 }}
            floatingIcon={
                <Animated.View style={{ transform: [{ rotate: rotateInterpolate }] }}>
                    <FontAwesomeIcon icon={faPlus} color={theme.colors.white['500']} fontSize={24} />
                </Animated.View>
            }
            shadow={{ shadowOpacity: 0.2, shadowOffset: { width: -1, height: 2 }, shadowColor: '#000000', shadowRadius: 4 }}
            listenKeyboard={true}
            dismissKeyboardOnPress={true}
            overrideWithAction={overrideWithAction}
        />
    );
}

const styleSheet = createStyleSheet(() => ({
    itemText: {
        fontWeight: '600',
        fontSize: 14,
        padding: 8,
        backgroundColor: '#3C3C3D',
        borderRadius: 4,
        color: '#ffffff',
        maxWidth: 220
    },
    itemButton: (borderColor: string) => ({
        width: 56,
        height: 56,
        borderRadius: 28,
        borderWidth: 2,
        borderColor,
        backgroundColor: '#ffffff',
        alignItems: 'center',
        justifyContent: 'center',
    }),
    dropShadow: {
        shadowColor: '#000000',
        shadowRadius: 4,
        shadowOpacity: 0.2,
        shadowOffset: {
            width: -1,
            height: 2
        }
    }
}));
